import { useMemo } from 'react';
import {
  _ClassesQuery,
  _RoomsQuery,
  SortDirection,
  use_ClassesQuery,
  use_DivisionsQuery,
  use_GradeGroupsQuery,
  use_GroupsQuery,
  use_PeopleQuery,
  use_RoomsQuery,
  use_SubjectContainersQuery,
  use_SubjectsQuery,
} from '../types/planung-graphql-client-defs';
import { sortClasses } from '../utils/sortHelper';
import { useAuthClaims } from './useAuthClaims';
import { useMemorizedCacheTag } from './useMemorizedCacheTag';
import { useUserConfigContext } from './useUserConfigContext';

export const useLoadBasicData = ({ pause }: { pause: boolean }) => {
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const { selectedSchoolYear } = useUserConfigContext();

  const classContext = useMemorizedCacheTag('CLASSES_FIXED');
  const teacherContext = useMemorizedCacheTag('PERSONS');
  const roomContext = useMemorizedCacheTag('ROOMS');
  const subjectContext = useMemorizedCacheTag('SUBJECTS');
  const subjectContainerContext = useMemorizedCacheTag('SUBJECT_CONTAINER');
  const gradeGroupContext = useMemorizedCacheTag('GRADE_GROUPS');
  const groupContext = useMemorizedCacheTag('GROUPS');
  const divisionContext = useMemorizedCacheTag('DIVISIONS');

  const [{ data: teacherData }, refetchTeacherData] = use_PeopleQuery({
    variables: {
      options: {
        sort: [{ active: SortDirection.Desc }, { lastName: SortDirection.Asc }, { firstName: SortDirection.Asc }],
      },
    },
    context: teacherContext,
    pause,
  });

  const filteredTeachers = useMemo(() => {
    return { people: teacherData?.people.filter((t) => t.active) ?? [] };
  }, [teacherData?.people]);

  const [{ data: _roomsData }] = use_RoomsQuery({
    variables: {
      options: {
        sort: [
          {
            name: SortDirection.Asc,
          },
          {
            classroom: SortDirection.Asc,
          },
        ],
      },
    },
    context: roomContext,
    pause,
  });

  const roomsData: _RoomsQuery = useMemo(() => {
    const sortedRooms = _roomsData?.rooms.sort((a, b) => {
      const numA = parseInt(a.name.match(/^\d+/)?.[0] || 'Infinity', 10);
      const numB = parseInt(b.name.match(/^\d+/)?.[0] || 'Infinity', 10);
      return numA - numB || a.name.localeCompare(b.name);
    });
    return {
      __typename: 'Query',
      rooms: sortedRooms ?? [],
    };
  }, [_roomsData?.rooms]);

  const [{ data: subjectData }] = use_SubjectsQuery({
    variables: {
      options: {
        sort: [
          {
            active: SortDirection.Desc,
          },
          {
            name: SortDirection.Asc,
          },
        ],
      },
    },
    context: subjectContext,
    pause,
  });

  const [{ data: subjectContainerData }] = use_SubjectContainersQuery({
    pause,
    context: subjectContainerContext,
  });

  const [{ data: gradeGroupData }] = use_GradeGroupsQuery({
    pause,
    context: gradeGroupContext,
    variables: {
      options: { sort: [{ name: SortDirection.Asc }, { shortName: SortDirection.Asc }] },
    },
  });

  const [{ data: _classesData }] = use_ClassesQuery({
    variables: {
      where: {
        schoolYear: {
          uuid: selectedSchoolYear?.uuid,
        },
        organization: {
          uuid: organizationUuid,
        },
      },
      options: {
        sort: [
          {
            sortName: SortDirection.Asc,
          },
        ],
      },
    },
    pause,
    context: classContext,
  });

  const classesData: _ClassesQuery = useMemo(() => {
    const sortedClasses = _classesData?.classes.sort(sortClasses);

    return {
      __typename: 'Query',
      classes: sortedClasses ?? [],
    };
  }, [_classesData?.classes]);

  const [{ data: groupsData }] = use_GroupsQuery({
    variables: {
      where: {
        divisionAggregate: {
          count_GT: 0,
        },
        division: {
          class: {
            schoolYear: {
              uuid: selectedSchoolYear?.uuid,
            },
          },
        },
      },
      options: {
        sort: [
          {
            name: SortDirection.Asc,
          },
        ],
      },
    },
    context: groupContext,
    pause,
  });

  const [{ data: divisionsData }] = use_DivisionsQuery({
    pause,
    variables: {
      where: {
        class: {
          schoolYear: {
            uuid: selectedSchoolYear?.uuid,
          },
        },
      },
    },
    context: divisionContext,
  });

  return {
    subjectData,
    subjectContainerData,
    roomsData,
    gradeGroupData,
    teacherData: filteredTeachers,
    allTeachers: teacherData,
    classesData,
    groupsData,
    divisionsData,
    refetchTeacherData,
  };
};
